// Fetching all conversation presets for now. Can try implementing pagination if we notice there are a lot of presets created.
// Pagination is difficult because we store the order in account data, and not the document, so running a query on partial data sorted would be difficult.
import { AccountData, Conversation } from "@markit/common.types";
import {
  getUserConversationPresetsSnap,
  getUserConversationsRef,
  getUserWishlistRef,
} from "./FirebaseUtils";
import { getDocs, limit, onSnapshot, orderBy, query, where } from "../firebase";
import { filterUndefinedValues } from "@markit/common.utils";
import { Dispatch, SetStateAction } from "react";
import { accountActions } from "../redux/slices/accountSlice";
import { fetchSingleUser } from "./FetchSingleData";

export const fetchConversationPresets = async (userData: AccountData) => {
  const presetsSnap = await getUserConversationPresetsSnap(userData.uid);
  const presets = presetsSnap.docs
    .map((doc) => doc.data())
    .sort(
      (a, b) =>
        userData.conversationPresetsOrder.indexOf(a.id) -
        userData.conversationPresetsOrder.indexOf(b.id)
    );
  return presets;
};

// Checks the participant's wishlist if they have that event in there wishmarks
const checkIsEventConversation = async (
  conversation: Conversation,
  eventId: string
) => {
  const userEventWishListRef = getUserWishlistRef(conversation.uid);
  const userEventWishListQuery = query(
    userEventWishListRef,
    where("eventId", "==", eventId),
    limit(1)
  );
  const userEventWishListSnapshot = await getDocs(userEventWishListQuery);
  return !userEventWishListSnapshot.empty;
};

// Filters conversations by event, returns all conversations that are associated with the event
// Fetches all conversations with recentTimestamp defined, and then checks the conversation participant's wishlist to see if they have the event in their wishlist
export const filterConversationsByEvent = async (
  userId: string,
  eventId: string
): Promise<Conversation[]> => {
  const conversationsRef = getUserConversationsRef(userId);
  const query_ = query(
    conversationsRef,
    where("recentTimestamp", "!=", ""),
    orderBy("recentTimestamp", "desc")
  );

  const foundConversations = (await getDocs(query_)).docs.map((doc) =>
    doc.data()
  );
  const filteredConversations: Conversation[] = filterUndefinedValues(
    await Promise.all(
      foundConversations.map(async (conversation) =>
        (await checkIsEventConversation(conversation, eventId))
          ? conversation
          : undefined
      )
    )
  );
  return filteredConversations;
};

export const liveFilterConversationsByEvent = (
  userId: string,
  eventId: string,
  setFilteredConversations: Dispatch<SetStateAction<Conversation[]>>,
  followingAccountData: AccountData[],
  dispatch: Dispatch<any>
) => {
  const conversationsRef = getUserConversationsRef(userId);
  const query_ = query(
    conversationsRef,
    where("recentTimestamp", ">", new Date().toISOString()),
    orderBy("recentTimestamp", "desc")
  );

  const unsubscribe = onSnapshot(query_, async (snapshot) => {
    const addedConversations = await Promise.all(
      snapshot.docChanges().map(async (change) => {
        if (change.type !== "added") {
          return;
        }
        const conversation = change.doc.data();
        if (await checkIsEventConversation(conversation, eventId)) {
          const userData = await fetchSingleUser(
            conversation.uid,
            followingAccountData
          );
          if (userData) {
            dispatch(
              accountActions.addMultipleFollowingAccountData([userData])
            );
          }
          return conversation;
        }
      })
    );

    const newConversations = filterUndefinedValues(addedConversations);
    if (newConversations.length === 0) {
      return;
    }

    setFilteredConversations((prev) => {
      const newUids = new Set(newConversations.map((c) => c.uid));

      // Update existing conversations in place
      const merged = prev.map((c) =>
        newUids.has(c.uid)
          ? newConversations.find((nc) => nc.uid === c.uid)!
          : c
      );

      // Append genuinely new conversations (contact has never messaged before)
      const brandNew = newConversations.filter(
        (nc) => !prev.some((c) => c.uid === nc.uid)
      );

      return [...merged, ...brandNew];
    });
  });

  return unsubscribe;
};
